import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { ROLE_OPTIONS, SUBROLE_OPTIONS, AVAILABILITY_OPTIONS } from "../constants";
import { createAgent } from "../../../../actions/agents/create-agent.actions";
import { useToken } from "../../../../hooks/useToken";
import { useSelector } from "react-redux";

export const CreateAgentForm = ({ onCancel, onCreated }) => {
  const { getTokenSilent } = useToken();
  const agentState = useSelector((s) => s.agent || {});

  const [form, setForm] = useState({
    name: "",
    mail_nickname: "",
    role: "",
    subrole: "",
    branch: "",
    phone: "",
    availability: AVAILABILITY_OPTIONS[0].value,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const getErrorMessage = (error) => {
    const rawError = error?._raw || error;
    const detail = rawError?.response?.data?.detail;
    const fallback = error?.message === '[object Object]'
      ? 'Error al crear agente'
      : (error?.message || 'Error al crear agente');

    let primaryMessage = '';
    let secondaryMessage = '';

    if (!detail) {
      return fallback;
    }

    if (typeof detail === 'string') {
      return detail;
    }

    if (typeof detail === 'object') {
      if (detail.message) {
        primaryMessage = detail.message;
      }

      const rawDetails = detail.details || rawError?.response?.data?.details;
      if (typeof rawDetails === 'string') {
        try {
          const parsed = JSON.parse(rawDetails);
          const nestedMessage = parsed?.error?.message || parsed?.message;
          if (nestedMessage) {
            secondaryMessage = nestedMessage;
          }
        } catch (parseError) {
          const match = rawDetails.match(/"message"\s*:\s*"([^"]+)"/);
          if (match && match[1]) {
            secondaryMessage = match[1];
          }
        }
      }

      if (primaryMessage && secondaryMessage) {
        return `${primaryMessage}\n${secondaryMessage}`;
      }

      if (secondaryMessage) {
        return secondaryMessage;
      }

      if (primaryMessage) {
        return primaryMessage;
      }
    }

    return fallback;
  };

  const handleSubmit = async () => {
    // basic validation required by API
    if (!agentState?.id_customer) {
      setSnackbar({ open: true, severity: 'error', message: 'No se encontró el customer' });
      return;
    }
    if (!form.name || !form.mail_nickname || !form.role) {
      setSnackbar({ open: true, severity: 'error', message: 'Nombre, mail nickname y rol son requeridos' });
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getTokenSilent();
      const payload = {
        id_customer: agentState.id_customer,
        display_name: form.name,
        mail_nickname: form.mail_nickname,
        role: form.role,
      };

      if (form.branch) payload.branch = form.branch;
      if (form.subrole) payload.subrole = String(form.subrole).toLowerCase();
      if (form.phone) payload.agent_phone_number = form.phone;

      const created = await createAgent({ token, payload });

      setSnackbar({ open: true, severity: 'success', message: 'Agente creado exitosamente' });
      if (onCreated) onCreated(created);
    } catch (err) {
      const message = getErrorMessage(err);
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Completa el siguiente formulario para crear un nuevo agente. Asegúrate
          de proporcionar toda la información correcta específicamente el correo
          electrónico ya que después no se podrá modificar.
        </Typography>

        <Box sx={{ mb: 3, mt: 1 }}>
          <TextField
            fullWidth
            required
            label="Nombre de agente"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Ej: Juan Pérez"
            size="small"
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            required
            label="Nickname correo"
            value={form.mail_nickname}
            onChange={handleChange('mail_nickname')}
            placeholder="Ej: juan.perez (sin dominio)"
            size="small"
            InputProps={{ endAdornment: <InputAdornment position="end">@yupio.com</InputAdornment> }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth required size="small">
            <InputLabel id="role-label">Rol</InputLabel>
            <Select
              labelId="role-label"
              label="Rol"
              value={form.role}
              onChange={handleChange('role')}
            >
              <MenuItem value="" disabled>
                Selecciona un rol
              </MenuItem>
              {ROLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="subrole-label">Subrol</InputLabel>
            <Select
              labelId="subrole-label"
              label="Subrol"
              value={form.subrole}
              onChange={handleChange('subrole')}
            >
              <MenuItem value="">Sin subrol</MenuItem>
              {SUBROLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Sucursal"
            value={form.branch}
            onChange={handleChange('branch')}
            placeholder="Sucursal del agente"
            size="small"
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Número de teléfono"
            value={form.phone}
            onChange={handleChange('phone')}
            placeholder="Teléfono del agente"
            size="small"
          />
        </Box>
        {/* <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="availability-label">Disponibilidad</InputLabel>
            <Select
              labelId="availability-label"
              label="Disponibilidad"
              value={form.availability}
              onChange={handleChange('availability')}
            >
              {AVAILABILITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box> */}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button onClick={onCancel} variant="outlined" disabled={isSubmitting}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creando…' : 'Crear Agente'}
          </Button>
        </Box>
        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert 
            variant="filled"
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
